import axios from "axios";
import { createContext, ReactNode, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorModal } from "../Components/Erro";
import { Loader } from "../Components/Loader";
import { IUsuario, useUsuario } from "../Hooks/UsuarioHook";
import { Api } from "../Utils/Api";
import { api_login_unico_url, app_base_url } from "../Utils/Urls";
import { RegistroProtocoloEletronico } from "../Utils/RegistroDemaisSistema";

export interface FiltrosPaginado {
  search?: string;
  status?: string;
  servidor?: string;
  sexo?: string;
  zonaRural?: string;
  idadeMin?: string;
  idadeMax?: string;
  profissao?: string;
  bairro?: string;
  cadastroInicio?: string;
  cadastroFim?: string;
}

// Defina o tipo de dados do contexto
interface ContextData {
  usuarios: IUsuario[];
  totalUsuarios: number;
  getUsuarios: (status?: string) => Promise<void>;
  getUsuariosPaginado: (
    params: FiltrosPaginado & { page: number; pageSize: number }
  ) => Promise<void>;
  getUsuariosTodosFiltrados: (params: FiltrosPaginado) => Promise<IUsuario[]>;
  getUsuarioDetalhes: (id: number) => Promise<void>;
  getDetalhesPorUsuario: (id: number) => Promise<IUsuario>;
  usuarioFocus: IUsuario;
  setUsuarioFocus: React.Dispatch<React.SetStateAction<IUsuario>>;
  getUsuariosPorSistema: (sistema_id: number) => Promise<void>;
  listarUsuariosPorSistema: (sistema_id: number) => Promise<IUsuario[]>;
  buscarUsuariosDaEntidade: (params: {
    search: string;
    page: number;
    pageSize: number;
    /** id do sistema: traz só quem ainda NÃO tem acesso a ele */
    semAcesso?: number;
  }) => Promise<{ lista: IUsuario[]; total: number }>;
  vincularUsuarioAoSistema: (
    usuario_id: number,
    sistema_id: number
  ) => Promise<void>;
  desvincularUsuarioDoSistema: (
    usuario_id: number,
    sistema_id: number
  ) => Promise<void>;
  atualiza_criaUsuario: (formData: FormData, replicar?: boolean) => Promise<any>;
  autoCadastro: (formData: FormData) => Promise<IUsuario>;
  setUsuarios: React.Dispatch<React.SetStateAction<IUsuario[]>>;
}

// Monta a query string ignorando valores vazios/"todos"
function montarQuery(params: Record<string, any>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (
      v !== undefined &&
      v !== null &&
      v !== "" &&
      v !== "todos" &&
      v !== "todas"
    ) {
      q.append(k, String(v));
    }
  });
  return q.toString();
}

// Mapeia a resposta do backend para IUsuario (montando a URL da foto)
function mapearUsuarios(lista: any[]): IUsuario[] {
  return (lista || []).map((element) => {
    if (element.foto && element.foto.length > 0) {
      element.foto = api_login_unico_url + "/foto-de-perfil/" + element.foto;
    } else {
      element.foto = null;
    }
    return { usuario: element } as IUsuario;
  });
}

// Crie o contexto com um valor inicial vazio
const UsuarioStorageContext = createContext<ContextData>({} as ContextData);

// Crie um hook personalizado para utilizar o contexto
function useUsuarioStorage() {
  const context = useContext(UsuarioStorageContext);
  if (!context) {
    throw new Error(
      "useUsuarioStorage deve ser usado dentro de um UsuarioStorageProvider"
    );
  }
  return context;
}

// Provedor do contexto
interface IProviderProps {
  children: ReactNode;
}

function UsuarioStorageProvider({ children }: IProviderProps) {
  // Defina seus estados e métodos aqui
  // Exemplo: const [usuario, setUsuarioStorage] = useState({} as IUsuarioStorage);
  const { token, logar, usuario, setManterAutenticado, manterAutenticado } =
    useUsuario();
  const [usuarioFocus, setUsuarioFocus] = useState({} as IUsuario);
  const [usuarios, setUsuarios] = useState([] as IUsuario[]);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alerta, setAlerta] = useState({ titulo: "", descricao: "" });
  function normalizeText(text: string) {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
  }


  async function getUsuarios(status?: string) {
    try {
      setLoading(true);
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const { data } = !status
        ? await Api.get(`/`)
        : await Api.get(`/status/${status}`);

      if (data.status) {
        const usuarios_ = [] as IUsuario[];
        for (let index = 0; index < data.data.length; index++) {
          const element = data.data[index];

          if (element.foto && element.foto.length > 0) {
            element.foto =
              api_login_unico_url + "/foto-de-perfil/" + element.foto;
          } else {
            element.foto = null;
          }

          usuarios_.push({} as IUsuario);
          usuarios_[index].usuario = element;

          usuarios_[index]._searchIndex = normalizeText(
            [
              element.nome,
              element.cpf,
              element.email,
              element.telefone,
              element.descricao,
              element.bairro
            ].join(" ")
          );

        }

        setUsuarios((prev) => usuarios_);
      } else {
        setAlerta({
          titulo: "Erro ao carregar usuários",
          descricao: data.mensagem,
        });
      }
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar usuários",
        descricao: JSON.stringify(e),
      });
      console.log(e);
      //"Não foi possível carregar os usuarios da entidade especificada!",
    } finally {
      setLoading(false);
    }
  }

  async function getUsuariosPaginado(
    params: FiltrosPaginado & { page: number; pageSize: number }
  ) {
    try {
      setLoading(true);
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const { data } = await Api.get(`/paginado?${montarQuery(params)}`);
      if (data.status) {
        setUsuarios(mapearUsuarios(data.data.usuarios));
        setTotalUsuarios(data.data.total ?? 0);
      } else {
        setAlerta({
          titulo: "Erro ao carregar usuários",
          descricao: data.mensagem,
        });
      }
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar usuários",
        descricao: JSON.stringify(e),
      });
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  // busca TODOS os registros filtrados (sem paginar) — usado no relatório
  async function getUsuariosTodosFiltrados(
    params: FiltrosPaginado
  ): Promise<IUsuario[]> {
    try {
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const { data } = await Api.get(
        `/paginado?${montarQuery({ ...params, page: 0, pageSize: 0 })}`
      );
      if (data.status) {
        return mapearUsuarios(data.data.usuarios);
      }
      return [];
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  /**
   * Mesma consulta do `getUsuariosPorSistema`, mas DEVOLVE a lista em vez de
   * jogar no estado compartilhado `usuarios` — usado pelo modal aberto a partir
   * do card do sistema, que não pode atropelar a listagem da tela de usuários.
   */
  async function listarUsuariosPorSistema(sistema_id: number): Promise<IUsuario[]> {
    Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    const { data } = await Api.get(
      `/sistema-usuario/usuario-por-sistema/${sistema_id}`
    );
    if (!data.status) throw new Error(data.mensagem || "Erro ao carregar os usuários");
    return mapearUsuarios(data.data);
  }

  /**
   * Busca usuários DA ENTIDADE para a aba "sem acesso" do modal do sistema.
   *
   * Vai no `/paginado` (mesmo endpoint da tela de usuários), que já filtra por
   * entidade e esconde o usuário de sistema. Pagina no servidor: a maior entidade
   * tem ~2500 usuários e renderizar tudo de uma vez trava o modal.
   *
   * `semAcesso` faz o próprio banco descontar quem já tem o vinculo, para a página
   * vir cheia — descontar no cliente aconteceria depois do LIMIT.
   */
  async function buscarUsuariosDaEntidade({
    search,
    page,
    pageSize,
    semAcesso,
  }: {
    search: string;
    page: number;
    pageSize: number;
    semAcesso?: number;
  }): Promise<{ lista: IUsuario[]; total: number }> {
    Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    const { data } = await Api.get(
      `/paginado?${montarQuery({ search, page, pageSize, semAcesso })}`
    );
    if (!data.status) throw new Error(data.mensagem || "Erro ao buscar usuários");
    return {
      lista: mapearUsuarios(data.data.usuarios),
      total: data.data.total ?? 0,
    };
  }

  /** Libera o acesso de um usuário a um sistema (cria o vínculo em SISTEMA_USUARIO) */
  async function vincularUsuarioAoSistema(usuario_id: number, sistema_id: number) {
    Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    const { data } = await Api.post(`/sistema-usuario`, { usuario_id, sistema_id });
    if (data && data.status === false) {
      throw new Error(data.mensagem || "Não foi possível liberar o acesso");
    }
  }

  /**
   * Remove o acesso. Usa a rota por usuario+sistema porque a lista de usuários do
   * sistema não traz o id do vínculo (SISTEMA_USUARIO.id) — só os dados do USUARIO.
   */
  async function desvincularUsuarioDoSistema(
    usuario_id: number,
    sistema_id: number
  ) {
    Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    const { data } = await Api.delete(
      `/sistema-usuario/usuario/${usuario_id}/sistema/${sistema_id}`
    );
    if (data && data.status === false) {
      throw new Error(data.mensagem || "Não foi possível remover o acesso");
    }
  }

  async function getUsuariosPorSistema(sistema_id: number) {
    try {
      setLoading(true);
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const { data } = await Api.get(
        `/sistema-usuario/usuario-por-sistema/${sistema_id}`
      );

      if (data.status) {
        const usuarios_ = [] as IUsuario[];
        for (let index = 0; index < data.data.length; index++) {
          const element = data.data[index];

          if (element.foto && element.foto.length > 0) {
            element.foto =
              api_login_unico_url + "/foto-de-perfil/" + element.foto;
          } else {
            element.foto = null;
          }

          usuarios_.push({} as IUsuario);
          usuarios_[index].usuario = element;
        }

        setUsuarios((prev) => usuarios_);
      } else {
        setAlerta({
          titulo: "Erro ao carregar usuários",
          descricao: data.mensagem,
        });
      }
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar usuários",
        descricao: JSON.stringify(e),
      });
      console.log(e);
      //"Não foi possível carregar os usuarios da entidade especificada!",
    } finally {
      setLoading(false);
    }
  }

  async function getDetalhesPorUsuario(id: number) {
    try {
      setLoading(true);
      const { data } = await Api.get(`/detalhes/${id}`);
      if (!data.status) {
        setAlerta({
          titulo: "Erro ao carregar detalhes do usuário",
          descricao: data.mensagem,
        });
      }

      if (data.data.usuario.foto && data.data.usuario.foto.length > 0) {
        data.data.usuario.foto =
          api_login_unico_url + "/foto-de-perfil/" + data.data.usuario.foto;
      }
      setUsuarioFocus(data.data);
      return data.data;
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar detalhes do usuário",
        descricao:
          "Não foi possível carregas os detalhes do usuario solicitado!",
      });
    } finally {
      setLoading(false);
    }
  }

  async function getUsuarioDetalhes(id: number) {
    try {
      setLoading(true);
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const { data } = await Api.get(`/`);

      if (data.status) {
        const usuarios_ = [] as IUsuario[];
        for (let index = 0; index < data.data.length; index++) {
          const element = data.data[index];

          if (element.foto && element.foto.length > 0) {
            element.foto =
              api_login_unico_url + "/foto-de-perfil/" + element.foto;
          } else {
            element.foto = null;
          }

          usuarios_.push({} as IUsuario);
          usuarios_[index].usuario = element;
        }

        setUsuarios((prev) => usuarios_);
      } else {
        setAlerta({
          titulo: "Erro ao carregar usuários",
          descricao: data.mensagem,
        });
      }
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar usuários",
        descricao: JSON.stringify(e),
      });
      console.log(e);
      //"Não foi possível carregar os usuarios da entidade especificada!",
    } finally {
      setLoading(false);
    }
  }

  async function atualiza_criaUsuario(formData: FormData, replicar?: boolean) {
    let id = 0;
    try {
      setLoading(true);

      const id = formData.get("id");
      const servidor = formData.get("servidor");
      const senhaValor = formData.get("senha");
      const senhaString =
        senhaValor instanceof File ? "" : (senhaValor as string);

      if (id == "undefined") formData.delete("id");

      const { data } =
        id !== null && id != "undefined"
          ? await Api.put(`/${replicar ? "replicar" : ""}`, formData)
          : await Api.post("/auto-cadastro", formData);

      if (data.status) {
        const usuario_helper = usuario;

        // usuario_helper.usuario = ;

        // setAlerta({
        //   titulo: `Dados de acesso ${
        //     id !== null ? "atualizados" : "criados"
        //   } com sucesso!`,
        //   descricao: data.mensagem,
        // });

        if (data.data.foto && data.data.foto.length > 0) {
          data.data.foto =
            api_login_unico_url + "/foto-de-perfil/" + data.data.foto;
        }
        RegistroProtocoloEletronico(data.data.id, token);
        if (!usuario.usuario) {
          await logar(data.data.id, data.data.senha, "id");
        } else if (usuario?.usuario?.id == data.data.id) {
          await logar(
            usuario_helper.usuario.id.toString(),
            senhaString ? senhaString : usuario.usuario.senha,
            "id"
          );
          const usuario_help = data.data;
          usuarioFocus.usuario = usuario_help;
          if (manterAutenticado && Number(id) == usuario.usuario.id) {
            setManterAutenticado(
              `{"id":"${usuario.usuario.id}","senha":"${usuario.usuario.senha}"}`
            );
          }
          setUsuarioFocus(usuarioFocus);
        }

      } else if (data.status == false) {
        setAlerta({ titulo: "Ops", descricao: data.mensagem });
      }
      return data.data;
    } catch (error) {
      // Trata erros de requisição
      setAlerta({
        titulo: "Ops",
        descricao: "Erro ao tentar atualizar o registro do usuário!",
      });
      console.error("Erro ao enviar requisição:", error);
    } finally {
      setLoading(false);
    }
  }

  async function autoCadastro(formData: FormData) {
    try {
      setLoading(true);

      formData.delete("id");
      const { data } = await Api.post("/auto-cadastro", formData);

      if (data.status) {
        setAlerta({
          titulo: `Usuário ${data.data.usuario} foi criado com sucesso!`,
          descricao:
            data.mensagem +
            "\n" +
            "Aguarde até que os Administradores liberem seu acesso! Você será notificado quando isso acontecer!",
        });
      } else {
        setAlerta({ titulo: "Ops", descricao: data.mensagem });
      }
      return data.data;
    } catch (error) {
      // Trata erros de requisição
      setAlerta({
        titulo: "Ops",
        descricao: "Erro ao tentar atualizar o registro do usuário!",
      });
      console.error("Erro ao enviar requisição:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <UsuarioStorageContext.Provider
      value={{
        usuarios,
        totalUsuarios,
        getUsuariosPaginado,
        getUsuariosTodosFiltrados,
        getUsuarios,
        getUsuarioDetalhes,
        getDetalhesPorUsuario,
        setUsuarioFocus,
        usuarioFocus,
        atualiza_criaUsuario,
        getUsuariosPorSistema,
        listarUsuariosPorSistema,
        buscarUsuariosDaEntidade,
        vincularUsuarioAoSistema,
        desvincularUsuarioDoSistema,
        autoCadastro,
        setUsuarios,
      }}
    >
      <Loader loading={loading} />
      <ErrorModal
        description={alerta.descricao}
        title={alerta.titulo}
        onClose={() => setAlerta({ descricao: "", titulo: "" })}
        open={alerta.titulo.length > 0 && alerta.descricao.length > 0}
      />
      {children}
    </UsuarioStorageContext.Provider>
  );
}

export { UsuarioStorageProvider, useUsuarioStorage };
